import {
  ACTION_SCHEMAS,
  TRIGGER_SCHEMAS,
  createDefaultActionConfig,
  createDefaultTrigger,
} from '../../../../services/automationRegistry'
import type {
  AutomationAction,
  AutomationActionType,
  AutomationCondition,
  AutomationConditionClause,
  AutomationConditionOp,
  AutomationStatus,
  AutomationTrigger,
  AutomationTriggerType,
} from '../../../../types/automation'
import { makeEmptyGraphDraft, type GraphDraft } from '../../../Automate/automationGraphDraft'

export type AutomationPayload = {
  automationVersion?: number
  graph: {
    name?: string
    version?: number
    status?: AutomationStatus
    triggers: AutomationTrigger[]
    constraint?: Record<string, unknown>
    if?: AutomationCondition
    then: AutomationAction[]
    else?: AutomationAction[]
  }
}

const CONDITION_OPS: AutomationConditionOp[] = ['EQ', 'NEQ', 'IN', 'NOT_IN', 'GT', 'GTE', 'LT', 'LTE', 'CONTAINS', 'NOT_CONTAINS', 'EXISTS', 'NOT_EXISTS']

const asRecord = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}

const asArray = <T,>(value: unknown): T[] => Array.isArray(value) ? value as T[] : []

const normalizeTrigger = (value: unknown): AutomationTrigger => {
  const trigger = asRecord(value)
  const type = typeof trigger.type === 'string' && trigger.type in TRIGGER_SCHEMAS ? trigger.type as AutomationTriggerType : 'NODE_CREATED'
  return { type, config: asRecord(trigger.config ?? createDefaultTrigger(type).config) } as AutomationTrigger
}

const normalizeAction = (value: unknown, index: number): AutomationAction => {
  const action = asRecord(value)
  const type = typeof action.type === 'string' && action.type in ACTION_SCHEMAS ? action.type as AutomationActionType : 'NOTIFY_USER'
  return {
    id: typeof action.id === 'string' && action.id ? action.id : `a${index + 1}`,
    type,
    config: asRecord(action.config ?? createDefaultActionConfig(type)),
  } as AutomationAction
}

const normalizeCondition = (value: unknown): AutomationCondition | undefined => {
  const condition = asRecord(value)
  const clauses = asArray<unknown>(condition.clauses).map((clause) => {
    const record = asRecord(clause)
    return {
      path: typeof record.path === 'string' ? record.path : 'payload.value',
      op: CONDITION_OPS.includes(record.op as AutomationConditionOp) ? record.op as AutomationConditionOp : 'EQ',
      value: record.value ?? true,
    } as AutomationConditionClause
  })
  return clauses.length > 0 ? { join: condition.join === 'OR' ? 'OR' : 'AND', clauses } : undefined
}

export const normalizeAutomationPayload = (value: unknown): AutomationPayload => {
  const payload = asRecord(value)
  const graph = asRecord(payload.graph)
  const normalized: AutomationPayload = {
    automationVersion: Number(payload.automationVersion) || 2,
    graph: {
      name: typeof graph.name === 'string' ? graph.name : undefined,
      version: Number(graph.version) || 2,
      status: graph.status === 'ACTIVE' || graph.status === 'DISABLED' ? graph.status : 'PAUSED',
      triggers: asArray<unknown>(graph.triggers).map(normalizeTrigger),
      then: asArray<unknown>(graph.then).map(normalizeAction),
    },
  }
  const condition = normalizeCondition(graph.if)
  const elseActions = asArray<unknown>(graph.else).map(normalizeAction)
  if (graph.constraint && typeof graph.constraint === 'object') normalized.graph.constraint = graph.constraint as Record<string, unknown>
  if (condition) normalized.graph.if = condition
  if (elseActions.length > 0) normalized.graph.else = elseActions
  return normalized
}

export const automationPayloadToDraft = (payload: AutomationPayload): GraphDraft => ({
  ...makeEmptyGraphDraft(),
  name: payload.graph.name ?? 'Untitled automation',
  description: '',
  triggers: payload.graph.triggers,
  constraint: payload.graph.constraint,
  if: payload.graph.if,
  then: payload.graph.then,
  else: payload.graph.else,
})

export const automationDraftToPayload = (draft: GraphDraft, source: AutomationPayload): AutomationPayload => {
  const normalized = normalizeAutomationPayload(source)
  return {
    automationVersion: normalized.automationVersion,
    graph: {
      name: draft.name,
      version: normalized.graph.version,
      status: normalized.graph.status,
      triggers: draft.triggers,
      ...(draft.constraint ? { constraint: draft.constraint } : {}),
      ...(draft.if ? { if: draft.if } : {}),
      then: draft.then,
      ...(draft.else && draft.else.length > 0 ? { else: draft.else } : {}),
    },
  }
}
